/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import React, { Fragment, useState } from 'react'
import { ListView } from 'antd-mobile'
import { isMobile } from 'react-device-detect'
import * as _ from 'lodash'
import { AdvEmpty } from '@components/AdvEmpty'
import { EMPTY_ICON } from '@constants/index'
import { AdvCommentListItem } from '@components/AdvComment/AdvCommentListItem'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
import useRootStore from '@store/useRootStore'
import { AdvComments } from '@components/AdvComment/index.style'
import { Schema$UserComment, Schema$UserCommentExt } from '@interface/comment'
import { FROM_VIEW_TYPE, USER_COMMENT_TYPE } from '@v2/constants/common'
import dayjs from 'dayjs'

interface CommentActivityContentProps {
  commentType: USER_COMMENT_TYPE
  fromViewType: FROM_VIEW_TYPE
  disableUserDetailInfo?: boolean
  baseMonth?: string,
}

export const CommentActivityContent: React.FC<CommentActivityContentProps> = observer((props) => {
  const { commentType, fromViewType, disableUserDetailInfo = false, baseMonth = null } = props
  const { commentStore, userStore } = useRootStore()
  const [isLoading, setIsLoading] = useState(false)

  const { myComments, myCommentHasMore } = commentStore

  const dataSource = new ListView.DataSource({
    rowHasChanged: (row1, row2) => row1 !== row2,
  })

  useOnMount(() => {
    fetch(true).catch(console.error)
  })

  useOnUnmount(() => {
    commentStore.resetMyComments()
  })

  const fetch = async (reset = false): Promise<void> => {
    try {
      setIsLoading(true)
      await commentStore.fetchMyComments(commentType, reset, baseMonth)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  const onUpdateComment = (updatedComment: Schema$UserComment): void => {
    commentStore.updateMyCommentHandler(updatedComment)
  }

  const onDeleteComment = (deletedComment: Schema$UserComment): void => {
    const result = commentStore.deleteMyCommentHandler(deletedComment)
    if (result) {
      if (baseMonth) {
        userStore.decreaseMyActivityMonthlyCommentCount(deletedComment.commentType)
      } else {
        const startAt = dayjs().startOf('months').toDate()
        const endAt = dayjs().endOf('months').toDate()
        const createdAt = dayjs(deletedComment.createdAt).toDate()

        if (startAt >= createdAt && startAt <= endAt) {
          userStore.decreaseMyActivityMonthlyCommentCount(deletedComment.commentType)
        }
      }
      userStore.decreaseMyActivityTotalCommentCount(deletedComment.commentType)
    }
  }

  const onToggleCommentLike = (updatedComment: Schema$UserComment): void => {
    commentStore.toggleMyCommentLikeHandler(updatedComment)
  }

  const renderRow = (row: Schema$UserCommentExt): React.ReactElement => {
    return (
      <AdvCommentListItem
        key={`user-comment-activity-item-${row.id}`}
        userComment={row}
        showMatchInfo
        hideReply
        fromViewType={fromViewType}
        callbackUpdated={onUpdateComment}
        callbackDeleted={onDeleteComment}
        callbackLiked={onToggleCommentLike}
        disableUserDetailInfo={disableUserDetailInfo}
        checkCommentTargetEnabled
      />
    )
  }

  const renderFooter = (): React.ReactElement => {
    let footer
    if (_.size(myComments) > 0) {
      footer = <Fragment/>
    } else {
      if (commentType === USER_COMMENT_TYPE.COMPANY_PICK_COMMENT) {
        footer = <AdvEmpty ico={EMPTY_ICON.COMPANY_PICK_COMMENT_ACTIVITY}/>
      } else {
        footer = <AdvEmpty ico={EMPTY_ICON.USER_PICK_COMMENT_ACTIVITY}/>
      }
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  const endReachedHandler = async () => {
    if (isLoading) {
      return
    }

    if (myCommentHasMore) {
      await fetch()
    }
  }

  return (
    <AdvComments className={isMobile ? 'pick-comment-popup' : 'pick-comment-popup pc'} style={{ height: 'calc(100% - 40px)' }}>
      <ListView
        css={{ height: '100%' }}
        dataSource={dataSource.cloneWithRows(myComments)}
        renderRow={renderRow}
        scrollRenderAheadDistance={500}
        renderFooter={renderFooter}
        onEndReached={endReachedHandler}
        onEndReachedThreshold={10}
        initialListSize={12}
        pageSize={20}
        className="pick-comments-list"
      />
    </AdvComments>
  )
})
